import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, Zap, Check, Download, ArrowLeft, Loader2 } from "lucide-react";
import api, { assetImage, formatIDR } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useCart } from "@/context/CartContext";
import { Watermark } from "@/components/Watermark";
import { AssetCard } from "@/components/AssetCard";
import { toast } from "sonner";

export default function AssetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, localizedTitle, localizedDesc } = useI18n();
  const { addItem, inCart, setOpen } = useCart();
  const [asset, setAsset] = useState(null);
  const [related, setRelated] = useState([]);
  const [license, setLicenseState] = useState("standard");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    api
      .get(`/assets/${id}`)
      .then((r) => {
        setAsset(r.data.asset);
        return api.get("/assets", { params: { category: r.data.asset.category, limit: 4 } });
      })
      .then((r) => setRelated(r.data.assets.filter((a) => a.id !== id).slice(0, 4)))
      .catch(() => navigate("/browse"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading || !asset) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 size={28} className="animate-spin text-slate-400" />
      </div>
    );
  }

  const price = license === "commercial" ? asset.price * 3 : asset.price;
  const added = inCart(asset.id);

  const handleAdd = () => {
    addItem(asset, license);
    toast.success(t("card.addToCart"));
  };
  const handleBuy = () => {
    if (!added) addItem(asset, license);
    navigate("/checkout");
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      <button
        onClick={() => navigate(-1)}
        className="mb-6 flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 transition"
        data-testid="back-btn"
      >
        <ArrowLeft size={16} /> {t("nav.browse")}
      </button>

      <div className="grid gap-10 lg:grid-cols-5">
        {/* Preview */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="lg:col-span-3"
        >
          <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
            <img
              src={assetImage(asset)}
              alt={localizedTitle(asset)}
              className="w-full object-cover"
              data-testid="asset-detail-image"
            />
            <Watermark rows={12} cols={7} />
          </div>
          <p className="mt-3 text-center text-xs text-slate-400">
            {t("card.preview")} · {asset.resolution}
          </p>
        </motion.div>

        {/* Info */}
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2">
            <span className="rounded-md bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
              {asset.category}
            </span>
            {asset.featured && (
              <span className="rounded-md bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
                ★ Featured
              </span>
            )}
          </div>
          <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-slate-900">
            {localizedTitle(asset)}
          </h1>
          <div className="mt-3 flex items-center gap-3">
            {asset.contributor_avatar && (
              <img src={asset.contributor_avatar} alt="" className="h-9 w-9 rounded-full object-cover" />
            )}
            <div>
              <p className="text-sm font-semibold text-slate-900">{asset.contributor_name}</p>
              <p className="flex items-center gap-1 text-xs text-slate-400">
                <Download size={11} /> {asset.downloads} {t("card.downloads")}
              </p>
            </div>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-slate-600">{localizedDesc(asset)}</p>

          {/* License */}
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-slate-900">{t("detail.license")}</h3>
            <div className="mt-3 space-y-2.5">
              {[
                { v: "standard", price: asset.price, desc: t("detail.standardDesc") },
                { v: "commercial", price: asset.price * 3, desc: t("detail.commercialDesc") },
              ].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setLicenseState(opt.v)}
                  className={`flex w-full items-center justify-between rounded-xl border p-4 text-left transition ${
                    license === opt.v ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`license-${opt.v}`}
                >
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{t(`detail.${opt.v}`)}</p>
                    <p className="text-xs text-slate-500">{opt.desc}</p>
                  </div>
                  <span className="font-mono text-sm font-bold text-slate-900">{formatIDR(opt.price)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Price + Actions */}
          <div className="mt-6 rounded-2xl bg-slate-900 p-6 text-white">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-slate-400">{t("cart.total")}</span>
              <span className="font-display text-3xl font-extrabold" data-testid="detail-price">
                {formatIDR(price)}
              </span>
            </div>
            <div className="mt-4 flex gap-3">
              <button
                onClick={handleAdd}
                className={`flex flex-1 items-center justify-center gap-2 rounded-xl py-3 text-sm font-semibold transition ${
                  added ? "bg-emerald-500 text-white" : "bg-white text-slate-900 hover:bg-slate-100"
                }`}
                data-testid="detail-add-cart-btn"
              >
                {added ? <Check size={16} /> : <ShoppingCart size={16} />}
                {added ? t("detail.inCart") : t("card.addToCart")}
              </button>
              <button
                onClick={handleBuy}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition"
                data-testid="detail-buy-btn"
              >
                <Zap size={16} /> {t("card.buyNow")}
              </button>
            </div>
          </div>

          {/* Details */}
          <div className="mt-6 rounded-2xl border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-900">{t("detail.details")}</h3>
            <dl className="mt-3 space-y-2 text-sm">
              {[
                [t("detail.resolution"), asset.resolution],
                [t("detail.fileSize"), asset.file_size],
                [t("detail.format"), asset.file_format],
                [t("detail.category"), asset.category],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <dt className="text-slate-500">{k}</dt>
                  <dd className="font-mono font-medium text-slate-900">{v}</dd>
                </div>
              ))}
            </dl>
            {asset.tags?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {asset.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-16">
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">
            {t("detail.relatedTitle")}
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((a, i) => (
              <AssetCard key={a.id} asset={a} index={i} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
